import React, { useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableBody from '@material-ui/core/TableBody'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TextField from '@material-ui/core/TextField'
import InputAdornment from '@material-ui/core/InputAdornment'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import SearchIcon from '@material-ui/icons/Search'
import DeleteIcon from '@material-ui/icons/Delete'
import CloseIcon from '@material-ui/icons/Close'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import { format } from 'date-fns'

var SVC_COLORS = { stt: '#22D3EE', tts: '#FB7185', livekit: '#60A5FA', llm: '#34D399', other: '#94A3B8' }
var SVC_LABELS = { stt: 'STT', tts: 'TTS', livekit: 'LiveKit', llm: 'LLM', other: 'Other' }
var PAGE_SIZE = 20

var useStyles = makeStyles(function(theme) {
  return {
    controls: {
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      flexWrap: 'wrap',
      borderBottom: '1px solid #f0f0f0',
    },
    search: { flex: 1, minWidth: 200 },
    count: { fontSize: 12, color: '#888' },
    tableWrap: { overflowX: 'auto' },
    th: {
      fontSize: 11,
      fontWeight: 700,
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      whiteSpace: 'nowrap',
      cursor: 'pointer',
      userSelect: 'none',
      padding: '10px 14px',
      background: '#fafafa',
    },
    thNoSort: {
      fontSize: 11,
      fontWeight: 700,
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      whiteSpace: 'nowrap',
      padding: '10px 14px',
      background: '#fafafa',
    },
    td: { padding: '10px 14px', fontSize: 12 },
    trHover: { cursor: 'pointer', '&:hover': { background: '#f8faff' } },
    sessionId: { fontFamily: 'monospace', fontSize: 11, color: '#1a2540', fontWeight: 600 },
    costHigh: { fontWeight: 700, color: '#FB7185' },
    costMid: { fontWeight: 700, color: '#FCD34D' },
    costLow: { fontWeight: 700, color: '#34D399' },
    chip: { height: 20, fontSize: 10, fontWeight: 700 },
    latencyPill: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '1px 6px',
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 600,
      marginRight: 3,
      fontFamily: 'monospace',
    },
    pagination: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 16px',
      borderTop: '1px solid #f0f0f0',
    },
    // Dialog
    detailHeader: { borderBottom: '1px solid #f0f0f0' },
    detailId: { fontFamily: 'monospace', fontSize: 11, color: '#555', marginTop: 4 },
    detailSection: { marginBottom: theme.spacing(2.5) },
    detailSectionTitle: {
      fontSize: 11,
      fontWeight: 700,
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      marginBottom: theme.spacing(1),
    },
    svcRow: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 12px',
      borderRadius: 8,
      marginBottom: 6,
      border: '1px solid #f0f0f0',
    },
    turnCard: {
      border: '1px solid #e8ecf4',
      borderRadius: 10,
      marginBottom: 8,
      overflow: 'hidden',
    },
    turnHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
      cursor: 'pointer',
      gap: 10,
      background: '#fafafa',
    },
    turnBadge: {
      width: 24,
      height: 24,
      borderRadius: 7,
      background: 'rgba(99,102,241,0.12)',
      border: '1px solid rgba(99,102,241,0.25)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      fontWeight: 700,
      color: '#6366f1',
      flexShrink: 0,
    },
  }
})

function fmtCost(v) {
  if (v == null) return '—'
  return '$' + Number(v).toFixed(5)
}

function fmtMs(ms) {
  if (ms == null) return null
  return Math.round(ms) + 'ms'
}

function fmtDur(min) {
  if (!min) return '0s'
  if (min < 1) return Math.round(min * 60) + 's'
  return min.toFixed(1) + ' min'
}

function shortId(id) {
  if (!id) return '—'
  if (id.length <= 12) return id
  return id.slice(0, 6) + '…' + id.slice(-4)
}

function costClass(v, classes) {
  if (v >= 0.15) return classes.costHigh
  if (v >= 0.05) return classes.costMid
  return classes.costLow
}

function SortIcon(props) {
  if (props.sortKey !== props.col) return null
  return props.sortDir === 'asc'
    ? <ArrowUpwardIcon style={{ fontSize: 12, marginLeft: 3, color: '#6366f1' }} />
    : <ArrowDownwardIcon style={{ fontSize: 12, marginLeft: 3, color: '#6366f1' }} />
}

function LatencyPill(props) {
  if (props.ms == null) return null
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '1px 6px', borderRadius: 999,
      fontSize: 10, fontWeight: 600,
      background: props.color + '14', border: '1px solid ' + props.color + '30',
      color: props.color, fontFamily: 'monospace', marginRight: 3,
    }}>
      {props.label} {fmtMs(props.ms)}
    </span>
  )
}

function TurnCard(props) {
  var classes = useStyles()
  var [open, setOpen] = useState(false)
  var turn = props.turn
  var svcRows = Object.entries(turn.serviceCosts || {}).filter(function(e) { return e[1] > 0 })

  return (
    <div className={classes.turnCard}>
      <div className={classes.turnHeader} onClick={function() { setOpen(!open) }}>
        <div className={classes.turnBadge}>{turn.index}</div>
        <div style={{ flex: 1 }}>
          <Typography style={{ fontSize: 12, fontWeight: 600, color: '#1a2540' }}>
            Turn {turn.index}
            {turn.timestamp && (
              <span style={{ fontSize: 10, fontWeight: 400, color: '#aaa', marginLeft: 8, fontFamily: 'monospace' }}>
                {format(turn.timestamp, 'HH:mm:ss')}
              </span>
            )}
          </Typography>
          <div style={{ marginTop: 3 }}>
            <LatencyPill label="STT" ms={turn.latencies && turn.latencies.stt} color="#22D3EE" />
            <LatencyPill label="LLM" ms={turn.latencies && turn.latencies.llm} color="#34D399" />
            <LatencyPill label="TTS" ms={turn.latencies && turn.latencies.tts} color="#FB7185" />
          </div>
        </div>
        <Typography style={{ fontSize: 13, fontWeight: 700, color: '#1a2540' }}>{fmtCost(turn.totalCost)}</Typography>
      </div>
      {open && svcRows.length > 0 && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0' }}>
          {svcRows.map(function(e) {
            var svc = e[0], cost = e[1]
            var color = SVC_COLORS[svc] || '#94A3B8'
            var lat = turn.latencies && turn.latencies[svc]
            return (
              <div key={svc} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ width: 3, height: 24, borderRadius: 999, background: color, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <Typography style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>{SVC_LABELS[svc] || svc}</Typography>
                  {lat != null && <Typography style={{ fontSize: 10, color: '#aaa', fontFamily: 'monospace' }}>{fmtMs(lat)}</Typography>}
                </div>
                <Typography style={{ fontSize: 12, fontWeight: 700, color: color, fontFamily: 'monospace' }}>{fmtCost(cost)}</Typography>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SessionDetail(props) {
  var classes = useStyles()
  var session = props.session
  if (!session) return null
  var totalCost = session.totalCost || 0
  var svcEntries = Object.entries(session.serviceCosts || {}).filter(function(e) { return e[1] > 0 })
  var turns = session.turns || []

  return (
    <Dialog open={Boolean(session)} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.detailHeader} disableTypography>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <Typography style={{ fontSize: 13, fontWeight: 700, color: '#1a2540' }}>Conversation Detail</Typography>
            <Typography className={classes.detailId}>{session.id}</Typography>
          </div>
          <IconButton size="small" onClick={props.onClose}><CloseIcon fontSize="small" /></IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>

        {/* Summary */}
        <div className={classes.detailSection}>
          <Typography className={classes.detailSectionTitle}>Summary</Typography>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { label: 'Total Cost', value: fmtCost(totalCost) },
              { label: 'Turns', value: session.turnCount || 0 },
              { label: 'Duration', value: fmtDur(session.durationMin) },
              { label: 'Date', value: session.startTime ? format(session.startTime, 'MMM d, HH:mm') : '—' },
            ].map(function(item) {
              return (
                <div key={item.label} style={{ padding: '10px 14px', background: '#f8faff', borderRadius: 8, border: '1px solid #e8ecf4' }}>
                  <Typography style={{ fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</Typography>
                  <Typography style={{ fontSize: 15, fontWeight: 700, color: '#1a2540', marginTop: 2 }}>{item.value}</Typography>
                </div>
              )
            })}
          </div>
        </div>

        {/* Identity */}
        {(session.userId || session.consumerId || session.firstName) && (
          <div className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>User Identity</Typography>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {session.firstName && <Chip size="small" label={(session.firstName + ' ' + session.lastName).trim()} />}
              {session.consumerId && <Chip size="small" label={'Consumer: ' + shortId(session.consumerId)} />}
              {session.userId && <Chip size="small" label={'User: ' + shortId(session.userId)} />}
              {session.deviceId && <Chip size="small" label={'Device: ' + shortId(session.deviceId)} />}
            </div>
          </div>
        )}

        {/* Service costs */}
        {svcEntries.length > 0 && (
          <div className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>Cost by Service</Typography>
            {svcEntries.map(function(e) {
              var svc = e[0], cost = e[1]
              var color = SVC_COLORS[svc] || '#94A3B8'
              var pct = totalCost > 0 ? (cost / totalCost * 100) : 0
              var lat = session.serviceLatencies && session.serviceLatencies[svc]
              return (
                <div key={svc} className={classes.svcRow}>
                  <div style={{ width: 3, height: 28, borderRadius: 999, background: color, flexShrink: 0, marginRight: 10 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <Typography style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
                        {SVC_LABELS[svc] || svc}
                        {lat && lat.avgMs != null && (
                          <span style={{ marginLeft: 8, fontSize: 10, color: '#aaa', fontFamily: 'monospace' }}>avg {fmtMs(lat.avgMs)}</span>
                        )}
                      </Typography>
                      <Typography style={{ fontSize: 12, fontWeight: 700, color: color, fontFamily: 'monospace' }}>{fmtCost(cost)}</Typography>
                    </div>
                    <div style={{ height: 3, borderRadius: 999, background: '#f0f0f0', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: pct + '%', background: 'linear-gradient(90deg, ' + color + '80, ' + color + ')', borderRadius: 999 }} />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Turns */}
        {turns.length > 0 && (
          <div className={classes.detailSection}>
            <Typography className={classes.detailSectionTitle}>Turns ({turns.length})</Typography>
            {turns.map(function(turn) {
              return <TurnCard key={turn.id} turn={turn} />
            })}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {props.onDelete && (
          <Button
            startIcon={props.isDeleting ? <CircularProgress size={14} /> : <DeleteIcon />}
            onClick={function() { props.onDelete(session) }}
            disabled={props.isDeleting}
            style={{ color: '#FB7185' }}
          >
            Delete Conversation
          </Button>
        )}
        <Button onClick={props.onClose} color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default function SessionsTab(props) {
  var classes = useStyles()
  var sessions = (props.data && props.data.sessions) || []
  var loading = props.loading
  var deletingIds = props.deletingIds || new Set()

  var [search, setSearch] = useState('')
  var [sortKey, setSortKey] = useState('startTime')
  var [sortDir, setSortDir] = useState('desc')
  var [page, setPage] = useState(0)
  var [selected, setSelected] = useState(null)

  // Auto-open session if passed from Overview
  React.useEffect(function() {
    if (!props.initialSessionId || !sessions.length) return
    var found = sessions.find(function(s) { return s.id === props.initialSessionId })
    if (found) setSelected(found)
  }, [props.initialSessionId, sessions])

  var filtered = useMemo(function() {
    var data = sessions.slice()
    if (search.trim()) {
      var q = search.toLowerCase()
      data = data.filter(function(s) {
        return (s.id || '').toLowerCase().includes(q) ||
               (s.userId || '').toLowerCase().includes(q) ||
               (s.firstName || '').toLowerCase().includes(q) ||
               (s.consumerId || '').toLowerCase().includes(q)
      })
    }
    data.sort(function(a, b) {
      var av, bv
      if (sortKey === 'startTime') { av = a.startTime ? a.startTime.getTime() : 0; bv = b.startTime ? b.startTime.getTime() : 0 }
      else if (sortKey === 'addedToCart' || sortKey === 'checkedOut') { av = a[sortKey] ? 1 : 0; bv = b[sortKey] ? 1 : 0 }
      else { av = a[sortKey] || 0; bv = b[sortKey] || 0 }
      return sortDir === 'asc' ? av - bv : bv - av
    })
    return data
  }, [sessions, search, sortKey, sortDir])

  var totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  var visible = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  function handleSort(key) {
    if (sortKey === key) setSortDir(function(d) { return d === 'asc' ? 'desc' : 'asc' })
    else { setSortKey(key); setSortDir('desc') }
    setPage(0)
  }

  var COLS = [
    { key: 'id', label: 'Conversation', sortable: false },
    { key: 'startTime', label: 'Date', sortable: true },
    { key: 'turnCount', label: 'Turns', sortable: true },
    { key: 'durationMin', label: 'Duration', sortable: true },
    { key: 'totalCost', label: 'Cost', sortable: true },
    { key: 'addedToCart', label: 'Cart', sortable: true },
    { key: 'checkedOut', label: 'Checkout', sortable: true },
    { key: 'pipeline', label: 'Pipeline', sortable: false },
    { key: 'actions', label: '', sortable: false },
  ]

  return (
    <>
      <Paper elevation={0} style={{ border: '1px solid #e8ecf4', borderRadius: 12, overflow: 'hidden' }}>
        {/* Controls */}
        <div className={classes.controls}>
          <TextField
            className={classes.search}
            size="small"
            variant="outlined"
            placeholder="Search by conversation ID, user, name…"
            value={search}
            onChange={function(e) { setSearch(e.target.value); setPage(0) }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 16, color: '#aaa' }} /></InputAdornment>
            }}
          />
          <Typography className={classes.count}>{filtered.length} conversation{filtered.length !== 1 ? 's' : ''}</Typography>
        </div>

        {/* Table */}
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLS.map(function(col) {
                  return (
                    <TableCell
                      key={col.key}
                      className={col.sortable ? classes.th : classes.thNoSort}
                      onClick={col.sortable ? function() { handleSort(col.key) } : undefined}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {col.label}
                        {col.sortable && <SortIcon sortKey={sortKey} col={col.key} sortDir={sortDir} />}
                      </span>
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLS.length} align="center" style={{ padding: 32 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLS.length} align="center" style={{ padding: 32, color: '#aaa' }}>
                    No conversations found
                  </TableCell>
                </TableRow>
              ) : visible.map(function(s) {
                var sttMs = s.serviceLatencies && s.serviceLatencies.stt && s.serviceLatencies.stt.avgMs
                var llmMs = s.serviceLatencies && s.serviceLatencies.llm && s.serviceLatencies.llm.avgMs
                var ttsMs = s.serviceLatencies && s.serviceLatencies.tts && s.serviceLatencies.tts.avgMs
                return (
                  <TableRow key={s.id} className={classes.trHover} onClick={function() { setSelected(s) }}>
                    <TableCell className={classes.td}>
                      <Typography className={classes.sessionId}>{shortId(s.id)}</Typography>
                    </TableCell>
                    <TableCell className={classes.td} style={{ color: '#555', whiteSpace: 'nowrap' }}>
                      {s.startTime ? format(s.startTime, 'MMM d, HH:mm') : '—'}
                    </TableCell>
                    <TableCell className={classes.td} align="center">{s.turnCount || 0}</TableCell>
                    <TableCell className={classes.td}>{fmtDur(s.durationMin)}</TableCell>
                    <TableCell className={classes.td}>
                      <span className={costClass(s.totalCost, classes)}>{fmtCost(s.totalCost)}</span>
                    </TableCell>
                    <TableCell className={classes.td} align="center">
                      <Chip size="small" className={classes.chip}
                        label={s.addedToCart ? '✓ Yes' : '✗ No'}
                        style={{ background: s.addedToCart ? 'rgba(52,211,153,0.12)' : '#f5f5f5', color: s.addedToCart ? '#34D399' : '#aaa' }}
                      />
                    </TableCell>
                    <TableCell className={classes.td} align="center">
                      <Chip size="small" className={classes.chip}
                        label={s.checkedOut ? '✓ Yes' : '✗ No'}
                        style={{ background: s.checkedOut ? 'rgba(99,102,241,0.12)' : '#f5f5f5', color: s.checkedOut ? '#6366f1' : '#aaa' }}
                      />
                    </TableCell>
                    <TableCell className={classes.td}>
                      <LatencyPill label="STT" ms={sttMs} color="#22D3EE" />
                      <LatencyPill label="LLM" ms={llmMs} color="#34D399" />
                      <LatencyPill label="TTS" ms={ttsMs} color="#FB7185" />
                    </TableCell>
                    <TableCell className={classes.td} onClick={function(e) { e.stopPropagation() }}>
                      {deletingIds.has(s.id) ? (
                        <CircularProgress size={14} style={{ color: '#FB7185' }} />
                      ) : (
                        <IconButton size="small" onClick={function(e) { e.stopPropagation(); props.onDeleteSession && props.onDeleteSession(s) }}
                          style={{ color: '#ddd' }}
                          onMouseOver={function(e) { e.currentTarget.style.color = '#FB7185' }}
                          onMouseOut={function(e) { e.currentTarget.style.color = '#ddd' }}
                        >
                          <DeleteIcon style={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={classes.pagination}>
            <Typography style={{ fontSize: 12, color: '#888' }}>Page {page + 1} of {totalPages}</Typography>
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: Math.min(totalPages, 7) }, function(_, i) { return i }).map(function(i) {
                return (
                  <button key={i} onClick={function() { setPage(i) }} style={{
                    width: 28, height: 28, borderRadius: 6, border: 'none',
                    background: page === i ? '#6366f1' : '#f5f5f5',
                    color: page === i ? '#fff' : '#555',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>
                    {i + 1}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </Paper>

      <SessionDetail
        session={selected}
        onClose={function() { setSelected(null) }}
        onDelete={props.onDeleteSession}
        isDeleting={selected && deletingIds.has(selected.id)}
      />
    </>
  )
}
